import { Link } from 'react-router-dom';
import { Clock, Tag } from 'lucide-react';
import { useArticles } from '@/lib/articles';
import { latestInsightsSection as defaultLatestInsightsSection } from '@/config-defaults';
import { useConfig } from '@/lib/siteData';

export function LatestInsights({ index }: { index?: number } = {}) {
  const config = useConfig('latest_insights_section', defaultLatestInsightsSection);
  // Reactive list — a bare getLatestArticles(3) froze this section at mount,
  // so newly published articles never reached the home page.
  const articles = useArticles().slice(0, 3);

  if (config.visible === false) return null;
  if (articles.length === 0) return null;

  return (
    <section id="insights" className="w-full py-16 md:py-20 bg-surface-elevated border-t border-surface-border-gray">
      <div className="w-full max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="font-mono-label text-xs uppercase tracking-[0.2em] text-text-light">
              {index != null && <span className="text-accent-blue mr-1">{String(index).padStart(2, '0')} — </span>}
              {config.eyebrow}
            </span>
            <h2 className="font-geist-mono text-3xl md:text-[2.5rem] font-medium text-text-primary mt-3 leading-[1.1] tracking-tight">
              {config.heading}
            </h2>
            {config.subheading && (
              <p className="font-body text-base text-text-gray mt-3 max-w-xl">
                {config.subheading}
              </p>
            )}
          </div>
          {config.ctaLabel && (
            <Link
              to="/insights"
              className="text-accent-blue text-sm font-medium hover:underline shrink-0 ml-4"
            >
              {config.ctaLabel} →
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {articles.map((article) => (
            <article
              key={article.slug}
              className="group flex flex-col p-6 rounded-xl border border-surface-border-gray bg-surface-elevated hover:border-text-light/40 transition-colors duration-150"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1 text-xs text-text-gray border border-surface-border-gray rounded px-2 py-0.5">
                  <Tag className="w-3 h-3" />
                  {article.tags[0]}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-text-gray">
                  <Clock className="w-3 h-3" />
                  {article.readTime} min
                </span>
              </div>
              <Link to={`/insights/${article.slug}`}>
                <h3 className="font-heading text-base font-semibold text-text-primary leading-snug mb-2 group-hover:text-accent-blue transition-colors">
                  {article.title}
                </h3>
              </Link>
              <p className="font-body text-sm text-text-gray leading-relaxed line-clamp-3 mb-4">
                {article.excerpt}
              </p>
              <div className="mt-auto flex items-center gap-3">
                <Link
                  to={`/insights/${article.slug}`}
                  className="text-accent-blue text-sm font-medium hover:underline"
                >
                  Read →
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    if (typeof navigator !== 'undefined' && navigator.share) {
                      navigator.share({
                        title: article.title,
                        url: `${window.location.origin}/insights/${article.slug}`,
                      }).catch(() => {});
                    }
                  }}
                  className="ml-auto text-text-light text-sm hover:text-text-primary transition-colors"
                  aria-label={`Share ${article.title}`}
                >
                  ↗ Share
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
